function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function matchInput(title, extraVariants) {
  // this function will receive a title like "Size: 32" and a list of inputs with dataset.variantSize like "32", "34", "36" and return the matching input;
  let size = title.replace(/[^0-9.]/g, ''); // Extract numeric part from title

  const match = Array.from(extraVariants).find(
    input => input.dataset.variantSize.replace(/[^0-9.]/g, '') === size
  );

  if (match) {
    return match;
  }
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

function formatMoney(cents, format) {
  "string" == typeof cents && (cents = cents.replace(".", ""));
  var value = "", placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || "${{amount}}";

  function formatWithDelimiters(number, precision, thousands, decimal) {
    if (thousands = thousands || ",", decimal = decimal || ".", isNaN(number) || null === number) return 0;
    var parts = (number = (number / 100).toFixed(precision)).split(".");
    return parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands) + (parts[1] ? decimal + parts[1] : "")
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case"amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case"amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case"amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case"amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case"amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case"amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'")
  }
  return formatString.replace(placeholderRegex, value)
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  connectedCallback() {
    if (this.dataset.autoplay === 'true') {
      this.loadContent(false);
    }
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {

        let isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari) {
          deferredElement.setAttribute('autoplay', 'autoplay'); // force autoplay for safari
          deferredElement.play();
        } else {
          setTimeout(() => {
            deferredElement.play();
          } , 200);
        }
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderID = this.slider.getAttribute('id');
    this.slidesId = this.sliderID.replace('Slider-', 'Slide-');
    this.sliderItems = this.querySelectorAll('[id^="' + this.slidesId + '"]');
    this.enableSliderLooping = false;
    if (this.slider.nextSibling) {
      this.firstSliderButtons = this.slider.nextElementSibling;
      if (this.firstSliderButtons.querySelectorAll('.slider-counter--nav').length > 0) {
        this.pageNav = this.firstSliderButtons.querySelector('.slider-counter--nav');
      }
      this.currentPageElement = this.firstSliderButtons.querySelector('.slider-counter--current');
      this.pageTotalElement = this.firstSliderButtons.querySelector('.slider-counter--total');
      if (this.tagName === 'SLIDER-COMPONENT') {
        this.prevButton = this.firstSliderButtons.querySelector('button[name="previous"]');
        this.nextButton = this.firstSliderButtons.querySelector('button[name="next"]');
      } else {
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');
      }
    }

    if (this.classList.contains('slide_up')) {
      this.querySelectorAll('.multicolumn-list__item').forEach((item) => {
        item.addEventListener('click', this.slideUpClick.bind(this));
      });
    }

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver(entries => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    if (this.pageNav) {
      this.sliderDots = this.firstSliderButtons.querySelectorAll('.slider-counter--nav .slider-counter--dot-button');
      this.sliderDots.forEach((dot) => dot.addEventListener('click', this.onButtonClick.bind(this)));
    }
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft - 1;
    this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);
    if (this.classList.contains('multicolumn-desktop-slider') && window.matchMedia('(min-width: 750px)').matches ) {
      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage;
    } else {
      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    }

    this.pageNav && this.updateNav(this.totalPages)
    this.update();
  }

  updateNav(items) {
    if (!this.pageNav) return;

    let totalDots = items;
    for (let i = 0; i < this.pageNav.children.length; i++) {
      let tableChild = this.pageNav.children[i];
      tableChild.classList.add('hidden');
    }
    if (totalDots > this.pageNav.children.length) {
      totalDots = this.pageNav.children.length;
    }
    for (let i = 0; i < totalDots; i++) {
      this.pageNav.getElementsByClassName(`dot-${i + 1}`)[0].classList.remove('hidden');
    }
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }

    if (this.pageNav) {
      for (let i = 0; i < this.pageNav.children.length; i++) {
        let tableChild = this.pageNav.children[i];
        tableChild.classList.remove('active');
      }
      this.pageNav.getElementsByClassName(`dot-${this.currentPage}`)[0].classList.add('active');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const currentTarget = event.currentTarget;
    const currentActive = currentTarget.parentElement.querySelector('.slider-counter--dot-button.active');
    let step = parseInt(event.currentTarget.dataset.step) || 1;
    if (currentTarget.classList.contains('slider-counter--dot-button')) {
      if (!currentTarget.classList.contains('active')) {
        let currentStep = parseInt(currentActive.dataset.step);
        if (step > currentStep) {
          let position = step - currentStep;
          this.slideScrollPosition = position * this.sliderItemOffset + this.slider.scrollLeft;
        } else {
          let position = currentStep - step;
          this.slideScrollPosition = this.slider.scrollLeft - position * this.sliderItemOffset;
        }
      }
    } else {
      this.slideScrollPosition =
        currentTarget.name === 'next'
          ? this.slider.scrollLeft + step * this.sliderItemOffset
          : this.slider.scrollLeft - step * this.sliderItemOffset;
    }

    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }

  mouseDownHandler(e) {
    this.slider.classList.add('grabbing');
    this.slider.style.userSelect = 'none';

    const pos = {
      left: this.slider.scrollLeft,
      top: this.slider.scrollTop,
      x: e.clientX,
      y: e.clientY,
    };

    const mouseMoveHandler = (e) => {
      const dx = e.clientX - pos.x;
      const dy = e.clientY - pos.y;

      this.slider.scrollTop = pos.top - dy;
      this.slider.scrollLeft = pos.left - dx;
    };

    const mouseUpHandler = () => {
      this.slider.classList.remove('grabbing');
      this.slider.style.removeProperty('user-select');

      document.removeEventListener('mousemove', mouseMoveHandler);
      document.removeEventListener('mouseup', mouseUpHandler);
    };

    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);
  }

  connectedCallback() {
    // Fix a bug where carousels are sometimes scrolled to the far left on page load
    if (this.dataset.reset_scroll == 'true') {
      this.slider.scrollLeft = 0;
    }
    this.slider.addEventListener('mousedown', this.mouseDownHandler.bind(this));
  }

  disconnectedCallback() {
    this.slider.removeEventListener('mousedown', this.mouseDownHandler.bind(this));
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
    queueMicrotask(() => this.ensureFirstAvailableInEachGroup());
  }

  isRadioEffectivelyDisabled(input) {
    if (!input) return true;

    // Real disabled
    if (input.disabled || input.matches(':disabled') || input.hasAttribute('disabled')) return true;

    // Theme "disabled" states applied via classes
    if (input.classList.contains('disabled')) return true;

    return false;
  }

  getFirstEffectivelyEnabledRadioByName(name) {
    const radios = Array.from(this.querySelectorAll(`input[type="radio"][name="${CSS.escape(name)}"]`));
    return radios.find((r) => !this.isRadioEffectivelyDisabled(r)) || null;
  }

  ensureFirstAvailableInEachGroup() {
    const radios = Array.from(this.querySelectorAll('input[type="radio"][name]'));
    const names = Array.from(new Set(radios.map((r) => r.name)));

    names.forEach((name) => {
      const checked = this.querySelector(`input[type="radio"][name="${CSS.escape(name)}"]:checked`);

      // If current is valid, keep it
      if (checked && !this.isRadioEffectivelyDisabled(checked)) return;

      const fallback = this.getFirstEffectivelyEnabledRadioByName(name);

      // If nothing available, clear selection for sanity
      if (!fallback) {
        radios
        .filter((r) => r.name === name)
        .forEach((r) => {
          r.checked = false;
        });
        return;
      }

      // Switch selection + trigger existing Shopify flow
      if (!fallback.checked) {
        fallback.checked = true;
        fallback.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }

    queueMicrotask(() => this.ensureFirstAvailableInEachGroup());
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantSelectMatrix extends VariantSelects {
  constructor() {
    super();
    this.manualInputs = this.querySelectorAll('input.manual-input');
    this.selectInputs = this.querySelectorAll('select');
    this.manualInputs.forEach((input) => {
      input.addEventListener('change', this.onChangeRadioInput);
    });

    this.selectInputs.forEach((input) => {
      input.addEventListener('change', this.onChangeSelect);
    })

    this.radioInputs = this.querySelectorAll('input[type="radio"]');
    this.radioInputs.forEach((input) => {
      input.addEventListener('click', this.clearManualInputs);
    });
  }

  findBiggerNextNumber(number, array) {
    let biggerNumber = null;
    const num = Number(number);
    array.forEach((currentNumber) => {
      const currentNum = Number(currentNumber);
      if (currentNum >= num) {
        if (biggerNumber === null || currentNum < biggerNumber) {
          biggerNumber = currentNum;
        }
      }
    });
    return biggerNumber;
  }

  findRadioInput = (event) => {
    let inputElement = event.target
    let posibleNumbers = [];
    inputElement.closest('fieldset').querySelectorAll('input[type="radio"]').forEach((input) => {
      if (input.dataset.radioMax) {
        posibleNumbers.push(input.dataset.radioMax);
      } else {
        let value = input.value.match(/\d+/)[0]
        input.dataset.radioMax = value;
        posibleNumbers.push(value);
      }
    });

    let radioInput = inputElement.closest('fieldset').querySelector('input[data-radio-max="' + this.findBiggerNextNumber(inputElement.value, posibleNumbers) + '"]');
    radioInput.checked = true
    radioInput.dispatchEvent(new Event('change', { bubbles: true }))
  }

  onChangeRadioInput = (event) => {
    if (event) {
      this.minMaxValidation(event);
      this.findRadioInput(event);
    }
    if (document.body.querySelectorAll('input[name="properties[Measurements]"]').length === 0) {
      this.createHiddenFormInputProperty('properties[Measurements]');
    } else {
      this.updateProperties('properties[Measurements]');
    }
  }

  onChangeSelect = (event) => {
    if (document.body.querySelectorAll('input[name="properties[Measurements]"]').length === 0) {
      this.createHiddenFormInputProperty('properties[Measurements]');
    } else {
      this.updateProperties('properties[Measurements]');
    }
  }

  clearManualInputs = (event) => {
    let inputElement = event.target
    inputElement.closest('fieldset').querySelectorAll('input.manual-input').forEach((input) => {
      input.value = '';
      this.updateProperties('properties[Measurements]');
    });
  }

  createHiddenFormInputProperty = (value) => {
    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.name = value;
    document.querySelector('product-form form').appendChild(hiddenInput);
    this.updateProperties(hiddenInput.name);
  }

  removeHiddenFormInputProperty = (value) => {
    document.querySelector(`input[name="${value}"]`).remove();
  }

  updateProperties = (value) => {
    let propertyValue = ''
    let removeProperty = false;
    this.manualInputs.forEach((input, i) => {
      if (input.value) {
        if (i > 0 && propertyValue !== '' ) propertyValue += ' ';
        if (input.dataset.labelOverwrite) {
          propertyValue += input.dataset.labelOverwrite + ': ' + input.value + 'cm | ';
        } else {
          propertyValue += input.closest('fieldset').querySelector('div.form__label span').textContent + ': ' + input.value + 'cm | ';
        }
        document.querySelector(`input[name="${value}"]`).value = propertyValue;
      }
    });

    this.selectInputs.forEach((select, i) => {
        if (select.value) {
          if (i > 0 && propertyValue !== '' ) propertyValue += ' ';
          if (select.dataset.labelOverwrite) {
            propertyValue += select.dataset.labelOverwrite + ': ' + select.value + 'cm | ';
          } else {
            propertyValue += select.closest('fieldset').querySelector('div.form__label span').textContent + ': ' + select.value + 'cm | ';
          }
          document.querySelector(`input[name="${value}"]`).value = propertyValue;
        }
    }

    )
    if (propertyValue === '') {
      removeProperty = true;
    } else {
      propertyValue = propertyValue.slice(0, -3); // remove last ' | '
      document.querySelector(`input[name="${value}"]`).value = propertyValue;
    }
    if (removeProperty) {
      this.removeHiddenFormInputProperty(value);
    }
  }


  minMaxValidation = (event) => {
    let inputElement = event.target

    if (inputElement.type === "number" && inputElement.max && inputElement.min ){
      let value = parseFloat(Number(inputElement.value).toFixed(1));
      inputElement.value = value // for 000 like input cleanup to 0
      let max = parseFloat(inputElement.max)
      let min = parseFloat(inputElement.min)
      if ( value > max ) inputElement.value = inputElement.max
      if ( value < min ) inputElement.value = inputElement.min
    }
  }
}

customElements.define('variant-selects-matrix', VariantSelectMatrix);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
    this.screenMode = this.dataset.screenMode;
  }

  connectedCallback() {
    this.checkScreenMode(this.screenMode);
    this.initializeRecommendations(this.dataset.productId);
  }
  checkScreenMode(mode) {
    let matchesDesktop = window.matchMedia('(min-width: 900px)').matches;
    let matchesMobile = window.matchMedia('(max-width: 899px)').matches;
    // matches means is desktop mode
    if (mode) {
      if (
        (mode === 'desktop' && !matchesDesktop) ||
        (mode === 'mobile' && !matchesMobile)
      ) {
        this.classList.add('disable');
      }

    }
  }

  initializeRecommendations(productId) {
    if (!productId) return;

    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        if (this.classList.contains('disable')) {
          return;
        } else {
          this.loadRecommendations(productId);
        }
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations[data-screen-mode="' + this.screenMode + '"]');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener('storefront:signincompleted', this._onStorefrontSignInCompleted);
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

class productCardGallery extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('mouseover', this.LoadImages.bind(this));

    let observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.25,
    };
    this.observer = new IntersectionObserver(this.handleIntersection, observerOptions);
  }
  connectedCallback() {
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0) {
      this.observer.observe(this);
    }
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.intersectionRatio > 0) {
        entry.target.LoadImages();
      }
    });
  }

  LoadImages() {
    if (!this.classList.contains('loaded')) {
      this.querySelectorAll('img').forEach((image) => {
        image.src = image.dataset.src;
      });
      this.classList.add('loaded');
    }
  }
}

customElements.define('rm-product-card-gallery', productCardGallery);

class OptionalTools extends HTMLElement {
  constructor() {
    super();
    this.selectAll = this.querySelector('[data-select-all]');
    this.kitBox = this.querySelector('[data-tools-details]');
    this.totalPriceElement = this.querySelector('[data-total-price]');
    this.singlePriceNumber = this.querySelectorAll('[data-price]');
    this.listItems = this.querySelectorAll('.bundle-item, .addon-item');
    // detect presence of attribute (works whether attribute has value or not)
    this.singleSelection = this.hasAttribute('data-single-selection');

    this.singlePriceNumber.forEach((item) => {
      item.addEventListener('change', (e) => this.enforceSingleSelection(e));
    });

    this.selectAll?.addEventListener('change', e => {
      this.selectAllInputs(e)
      this.getMatchingProductToDeselect(e.target)
    })

    this.singlePriceNumber.forEach((item, index) =>{
      item.addEventListener('change', e => {
        this.calculatePrice(true);
        this.checkIfAnySelectNotChecked();
      });
    });

  }

  enforceSingleSelection(event) {
    if (!this.singleSelection) return;

    const current = event?.target;
    if (!current || current.type !== 'checkbox') return;

    // Only act when a box is being checked (not unchecked)
    if (!current.checked) return;

    this.singlePriceNumber.forEach((input) => {
      if (input !== current && input.checked) {
        input.checked = false;
      }
    });
  }

  variantChangeUnsubscriber = undefined;

  connectedCallback() {
    if (this.listItems.length > 0) {
      let containsMatchingVariants = false;
      this.listItems.forEach((entry) => {
        let extraVariants = this.querySelectorAll('.extra-variants input[type="hidden"]');
        if (extraVariants.length > 0) {
          containsMatchingVariants = true;
        }
      })

      if (containsMatchingVariants) {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          this.matchAllItems(event.data.variant.title);
        });

        this.matchAllItems(document.querySelector('variant-selects .variant-size input:checked').value);
      }
    }

    this.calculatePrice(false);

    if (this.hasAttribute('lightbox')){
      this.initLightbox(Array.from(JSON.parse(this.getAttribute('lightbox'))));
    }
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
      this.variantChangeUnsubscriber = undefined;
    }
  }

  matchAllItems(title) {
    this.listItems.forEach((entry) => {
      let extraVariants = entry.querySelectorAll('.extra-variants input[type="hidden"]');
      if (extraVariants.length > 0) {
        this.printData(entry, matchInput(title, extraVariants));
      }
    });
  }

  printData(mainItem, matchItem) {
    const mainCheckbox = mainItem.querySelector('input[type="checkbox"]')
    const details = mainItem.querySelector('[data-details]');
    mainCheckbox.dataset.price = matchItem ? matchItem.dataset.variantPrice : 0;
    mainCheckbox.dataset.variant = matchItem ? matchItem.dataset.variantId : 0;

    if (!matchItem) {
      mainCheckbox.disabled = true;
      mainCheckbox.checked = false;
    } else {
      mainCheckbox.disabled = false;
    }

    if (matchItem) {
      details.querySelector('[data-variant-title]').innerText = matchItem.dataset.variantTitle;
      details.querySelector('[data-variant-price]').innerText = formatMoney(matchItem.dataset.variantPrice, window.moneyFormat);
    }

    let discount = parseFloat(this.totalPriceElement.dataset.discount);
    if (discount) {
      details.querySelector('.bundle-price span').innerText = formatMoney(matchItem.dataset.variantPrice - (matchItem.dataset.variantPrice * (discount / 100)), window.moneyFormat);
    }
    this.calculatePrice(true)
  }

  initLightbox(lightboxContent) {
    const button = document.querySelector('.roomix__title-view-button');
    if (button) {
      button.addEventListener('click', () => {
        new Fancybox(lightboxContent, {
          Thumbs : {
            type: "classic"
          },
          caption: (fancybox, slide) => {
            const caption = slide.caption || "";
            const title = lightboxContent[slide.index].title

            return (
              `<h3>${title}</h3>
                <p>${caption}</p>`
            );
          }
        })
      })
    }
  }

  checkIfAnySelectNotChecked() {
    this.singlePriceNumber.forEach((item, index) =>{
      if (!item.checked && this.selectAll) {
        this.selectAll.checked = false;
      }
    });

    let toolCheckboxes = this.querySelectorAll('.kit-list input');
    let toolCheckboxesChecked = this.querySelectorAll('.kit-list input:checked');

    if (toolCheckboxes.length === 0 ) {
      this.kitBox.classList.remove('active');
    } else {
      this.kitBox.classList.add('active');
    }

    if (toolCheckboxes.length === toolCheckboxesChecked.length) {
      this.selectAll.checked = true;
    }
  }

  getMatchingProductToDeselect(element) {
    let variantInputsElementsArray = element.closest('.tools-details').querySelectorAll('[data-variant]');
    variantInputsElementsArray.forEach((variantFromSelectedItem, index) => {
      const matchingInputsArray = document.querySelectorAll(`input[data-variant="${variantFromSelectedItem.dataset.variant}"]:checked`);
      if (matchingInputsArray.length > 1) {
        matchingInputsArray.forEach((item, index) => {
          if (item !== variantFromSelectedItem) {
            if (item.closest('optional-tools')) {
              item.checked = false;
            }
            if (item.closest('step-extra-product')) {
              let stepExtraProduct = item.closest('step-extra-product');
              stepExtraProduct.dataset.price = 0;
              stepExtraProduct.querySelector('input[data-variant="0"]').checked = true;
            }
          }
        });
      }
    });
  }

  selectAllInputs(e) {
    if (e.target.checked) {
      this.singlePriceNumber.forEach((item, index) =>{
        if (!item.disabled) {
          item.checked = true
        }
      });
      this.kitBox.classList.add('active');
    } else {
      this.singlePriceNumber.forEach((item, index) =>{
        item.checked = false
      });
      this.kitBox.classList.remove('active');
    }
    this.calculatePrice(true)
  }

  calculatePrice(updateMirror) {
    let totalPrice = 0;
    this.singlePriceNumber.forEach((item, index) =>{
      if (item.checked) {
        totalPrice += parseInt(item.dataset.price);
      }
    });

    if (this.totalPriceElement.dataset.discount) {
      let discount = parseFloat(this.totalPriceElement.dataset.discount);
      let discountedPrice = totalPrice - (totalPrice * (discount / 100));
      this.totalPriceElement.innerHTML = formatMoney(discountedPrice, window.moneyFormat) + ' <s class="original-price"> ' + formatMoney(totalPrice, window.moneyFormat) + '</s> ';
    } else {
      this.totalPriceElement.innerHTML = formatMoney(totalPrice, window.moneyFormat);
    }
    if (totalPrice === 0) {
      this.totalPriceElement.classList.add('hidden');
    } else {
      this.totalPriceElement.classList.remove('hidden');
    }
    setTimeout(() => {
      updateMirror && this.closest('.product').querySelector('price-mirror')?.updatePrice();
    }, 300)
  }
}

customElements.define('optional-tools', OptionalTools);

class OptionalItems extends HTMLElement {
  constructor() {
    super();
    this.elementInput = this.querySelectorAll('[data-variant]')
    this.elementQty = this.querySelector('[name="extra-quantity"]')
    this.selectedName = this.querySelector('.selected-product-name')
    this.elementTotalPrice = this.querySelector('[data-total-price]')
    this.listItems = this.querySelectorAll('.step-item');

    this.elementInput.forEach((item, index) =>{
      item.addEventListener('change', e => {
        publish(PUB_SUB_EVENTS.optionalSelectionChange, { source: item });
        this.calculateElementPrice(e)
        this.displayName();
        const quantityHandle = this.querySelector('.quantity-handle');
        if (this.querySelectorAll('[data-match-quantity]').length < 1) {
          if (e.target.dataset.productName === 'No Paint') {
            quantityHandle.classList.add('hidden');
          } else {
            if (quantityHandle.dataset.needsQuantity === 'true') {
              quantityHandle.classList.remove('hidden');
            }
          }
        }
      });
    });
    if (this.elementQty) {
      this.elementQty.addEventListener('change', e => {
        this.calculateElementPrice(true);
      })
    }
  }

  connectedCallback() {
    if (this.listItems.length > 0) {
      let containsMatchingVariants = false;
      this.listItems.forEach((entry) => {
        let extraVariants = this.querySelectorAll('.extra-variants input[type="hidden"]');
        if (extraVariants.length > 0) {
          containsMatchingVariants = true;
        }
      })

      if (containsMatchingVariants) {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          this.matchAllItems(event.data.variant.title);
        });

        this.matchAllItems(document.querySelector('variant-selects .variant-size input:checked').value);
      }
    }

    this.calculateElementPrice(false)
    this.displayName();
    if (this.querySelectorAll('[data-match-quantity]').length < 1) {
      this.querySelector('.quantity-handle').classList.add('hidden');
    }
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
      this.variantChangeUnsubscriber = undefined;
    }
  }

  ensureFallbackRadioSelection(groupInput) {
    const name = groupInput?.name;
    if (!name) return;

    // Only operate within this component instance
    const radios = Array.from(this.querySelectorAll(`input[type="radio"][name="${CSS.escape(name)}"]`));
    if (!radios.length) return;

    const checked = radios.find((r) => r.checked);

    // If something is checked and still enabled, do nothing
    if (checked && !checked.disabled) return;

    // Pick the first enabled radio in the same group
    const fallback = radios.find((r) => !r.disabled);
    if (fallback) {
      fallback.checked = true;
      fallback.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      // No enabled options left; ensure none are checked
      radios.forEach((r) => {
        r.checked = false;
      });
    }
  }

  ensureAllRadioGroupsHaveSelection() {
    // For each radio group (by name), ensure a valid selection exists
    const radios = Array.from(this.querySelectorAll('input[type="radio"][name]'));
    const names = Array.from(new Set(radios.map((r) => r.name)));

    names.forEach((name) => {
      const anyInGroup = this.querySelector(`input[type="radio"][name="${CSS.escape(name)}"]`);
      this.ensureFallbackRadioSelection(anyInGroup);
    });
  }

  matchAllItems(title) {
    this.listItems.forEach((entry) => {
      const extraVariants = entry.querySelectorAll('.extra-variants input[type="hidden"]');
      if (extraVariants.length < 1) return;

      const match = matchInput(title, extraVariants);

      const input = entry.querySelector('input[type="radio"]');
      const label = input?.closest('label');

      if (!match) {
        if (input) {
          // Make it non-selectable and non-addable
          input.checked = false;
          input.disabled = true;
          input.dataset.variant = '0';
          input.dataset.price = '0';
        }
        label?.classList.add('is-disabled');
        return;
      }

      // Re-enable when a match exists again
      if (input) input.disabled = false;
      label?.classList.remove('is-disabled');

      this.printData(entry, match);
    });

    // Ensure we fall back to the first enabled radio in each group after disabling
    this.ensureAllRadioGroupsHaveSelection();

    this.calculateElementPrice(false);
    this.displayName();
  }

  printData(mainItem, matchItem) {
    if (!matchItem) return;
    const mainCheckbox = mainItem.querySelector('input[type="radio"]')
    const details = mainItem.closest('label').querySelector('.label-text');
    mainCheckbox.dataset.price = matchItem ? matchItem.dataset.variantPrice : 0;
    mainCheckbox.dataset.variant = matchItem ? matchItem.dataset.variantId : 0;
    if (matchItem) {
      details.querySelector('[data-variant-title]').innerText = matchItem.dataset.variantTitle;
      details.querySelector('[data-variant-price]').innerText = formatMoney(matchItem.dataset.variantPrice, window.moneyFormat);
    }

    this.calculateElementPrice(false)
  }

  displayName() {
    const productName = this.querySelector('input[type="radio"]:checked');
    if (!productName) return;
    if (this.querySelectorAll('[data-match-quantity]').length > 1 && productName.dataset.productName) {
      this.selectedName.innerHTML = this.elementQty.value + ' of ' + productName.dataset.productName;
    } else {
      this.selectedName.innerHTML = productName.dataset.productName;
    }
  }

  calculateElementPrice = (updateTotal) => {

    const selectedItem = this.querySelector('input[type="radio"]:checked');
    if (!selectedItem) return;

    let quantity = this.elementQty ? this.elementQty.value : 1;
    selectedItem.dataset.quantity = quantity;
    this.elementTotalPrice.innerHTML =  formatMoney(selectedItem.dataset.price * quantity, window.moneyFormat);
    this.displayName();
    if (updateTotal) {
      this.closest('.product').querySelector('price-mirror')?.updatePrice();
    }
  }
}
customElements.define('optional-items', OptionalItems);

class stepExtraProduct extends HTMLElement {
  constructor() {
    super();
    this.elementInput = this.querySelectorAll('[data-variant]')
    this.currencySymbol = this.dataset.currencySymbol;
    this.mainInputElement = this.elementInput[0];
    this.elementLabel = this.mainInputElement.nextElementSibling;
    this.selectedName = this.querySelector('.selected-product-name')
    this.extraVariants = this.querySelectorAll('.extra-variants input[type="hidden"]');

    this.elementInput.forEach((item, index) =>{
      item.addEventListener('change', e => {
        this.getMatchingProductToDeselect(item)
        this.calculateElementPrice(e)
      });
    });
  }

  variantChangeUnsubscriber = undefined;

  printData(match) {
    this.mainInputElement.dataset.variant = match.dataset.variantId;
    this.mainInputElement.dataset.variantPrice = match.dataset.variantPrice;
    this.mainInputElement.dataset.variantTitle = match.dataset.variantTitle;
    this.elementLabel.querySelector('.item-image img').src = match.dataset.variantImage;
    let mainTitleElement = this.elementLabel.querySelector('.variant_title')
    mainTitleElement.querySelector('b').textContent = match.dataset.variantTitle;
    mainTitleElement.querySelector('span').textContent = '+' + this.currencySymbol + match.dataset.variantPrice;
  }

  connectedCallback() {
    if (this.extraVariants.length > 0) {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        this.printData(matchInput(event.data.variant.title, this.extraVariants));
      });
      this.matchCurrentVariant();
    }
  }

  matchCurrentVariant() {
    if (this.hasAttribute('data-match-variant')) {
      const selectedVariant = document.querySelector('variant-selects input:checked').value;
      this.printData(matchInput(selectedVariant, this.extraVariants));
    }
  }

  calculateElementPrice = (e) => {
    if (e instanceof Event) {
      this.dataset.price =  e.target.dataset.variantPrice;
      this.dataset.quantity = e.target.dataset.itemQuantity || 1;
    } else {
      this.dataset.price =  e.dataset.variantPrice;
      this.dataset.quantity = e.dataset.itemQuantity || 1;
    }
    setTimeout(() => {
      this.closest('.product').querySelector('price-mirror')?.updatePrice();
    }, 300)
  }

  getMatchingProductToDeselect(element) {
    let variantID = element.dataset.variant
    if (variantID !== '0') {
      let matchingInputsArray = document.querySelectorAll(`input[data-variant="${variantID}"]:checked`);
      if (matchingInputsArray.length > 1) {
        console.log(matchingInputsArray);
        matchingInputsArray.forEach((item, index) => {
          if (item !== element) {
            let optionalTools = item.closest('optional-tools.mode-bundle');
            if (item.closest('optional-tools.mode-bundle')) {
              optionalTools.querySelectorAll('input').forEach((input, index) => {
                input.checked = false;
                optionalTools.calculatePrice(true)
              })
            } else {
              if (item.closest('optional-tools')) {
                item.checked = false;
                optionalTools.calculatePrice(true)
              }
            }
          }
        });
      }
    }
  }
}
customElements.define('step-extra-product', stepExtraProduct);

function parseLocalPrice(str) {
  let cleaned = str.replace(/[€£\s\n]/g, "").trim();
  if (cleaned.match(/,\d{2}$/)) {
    // German format: 434,95 or 1.234,95
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else {
    // UK format: 369.00 or 36,999.00
    cleaned = cleaned.replace(/,/g, "");
    }
  return parseFloat(cleaned) || 0;
  }

class priceMirror extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Re-query on reconnect (HTML refresh / node swap) so cached NodeLists stay valid.
    this.updatePrice();
  }

  disconnectedCallback() {
    // Avoid holding stale references across swaps.
    this.kitTotalItems = null;
  }

  updatePrice() {
    const kitTotalItems = document.querySelectorAll('[data-total-price]');
    let sum = 0;
    let productPriceContainer = document.querySelector('.price-holder')
    let productPriceElement = productPriceContainer.querySelector('.price-item--regular')
    let productPrice = parseLocalPrice(productPriceElement?.innerHTML || '');
    let comparedPrice = 0;
    if (productPriceContainer.querySelectorAll('.price-item--compared').length > 0) {
      comparedPrice = parseLocalPrice(productPriceContainer.querySelector('.price-item--compared').innerHTML);
    }
    kitTotalItems.forEach((price) => {
      const parsed = parseLocalPrice(price.innerHTML);
      if (!isNaN(parsed)) {
        sum += parsed;
      }
    })
    if (document.querySelectorAll('roomix-dimensions').length > 0) {
      if (document.querySelector('roomix-dimensions').dataset.price) {
        let quantity = 1;
        if (document.querySelector('roomix-dimensions quantity-input input')){
          quantity = parseInt(document.querySelector('roomix-dimensions quantity-input input').value)
        }
        sum += parseFloat(document.querySelector('roomix-dimensions').dataset.price) * quantity;
      }
      productPrice = 0;
    }

    if (document.querySelectorAll('step-extra-product').length > 0) {
      document.querySelectorAll('step-extra-product').forEach((item) => {
        if (item.dataset.price) {
          let quantity = item.dataset.quantity;
          sum += parseFloat(item.dataset.price) * quantity;
        }
      })
    }

    if (document.querySelector('roomix-design-tool-summary')) {
      document.querySelectorAll('roomix-design-tool-summary input:checked').forEach(((checkbox, index) => {
        sum += parseFloat(checkbox.parentElement.querySelector('.price').innerHTML.replace(/[€,£,+]/g, ""));
      }))
    }

    let totalPrice = (productPrice + sum).toFixed(2);
    if (comparedPrice > 0) {
      let totalComparedPrice = (comparedPrice + sum).toFixed(2);
      //this.innerHTML = `<s>£${totalComparedPrice}</s> <span class="price-item--last color-scheme-3">£${totalPrice}</span>`
      this.innerHTML = `<span class="price-item--last">${window.currencySymbol}${totalPrice}</span>`
    } else {
      this.innerHTML = `<span>${window.currencySymbol}${totalPrice}</span>`
    }
    let totalPriceNoDecimal = totalPrice.toString().replace('.', '');


    if (document.querySelector('roomix-summary')) {
      if (typeof document.querySelector('roomix-summary').updateSteps === "function") {
        document.querySelector('roomix-summary').updateSteps();
      }
    }

    const klarnaElement = document.querySelector('.klarna-banner');
    if (klarnaElement) {
      klarnaElement.querySelector('.klarna_breakdown').innerHTML = window.currencySymbol + (totalPrice / 3).toFixed(2);
    }
  }
}
customElements.define('price-mirror', priceMirror);

// In the same file where `RmHotspotsSliderComponent` is defined:

class RmHotspotsSliderComponent extends SliderComponent {
  constructor() {
    super();

    this._activeIndex = 0;
    this._hotspots = [];
    this._slides = [];

    this._rmScrollT = null;
    this._rmNavToken = 0;
    this._rmIgnoreScrollSelectionUntil = 0;

    this._onPrevCapture = this._onPrevCapture.bind(this);
    this._onNextCapture = this._onNextCapture.bind(this);
    this._onScrollCapture = this._onScrollCapture.bind(this);
    this._onHotspotClickCapture = this._onHotspotClickCapture.bind(this);
    this._onHotspotKeydownCapture = this._onHotspotKeydownCapture.bind(this);
  }

  connectedCallback() {
    super.connectedCallback?.();
    if (!this.slider) return;

    this._cacheNodes();
    this._bindCaptureInterceptors();
    this._initFromMarkup();
  }

  disconnectedCallback() {
    this._unbindCaptureInterceptors();
    window.clearTimeout(this._rmScrollT);
    super.disconnectedCallback?.();
  }

  _cacheNodes() {
    this._hotspots = Array.from(this.querySelectorAll('[data-hotspot]'));
    this._slides = this._getSlides();
  }

  _getSlides() {
    const byId = Array.from(this.querySelectorAll('[id^="' + this.slidesId + '"]'));
    if (byId.length) return byId;

    const byData = Array.from(this.querySelectorAll('[data-slide]'));
    if (byData.length) return byData;

    return Array.from(this.sliderItems || []);
  }

  _bindCaptureInterceptors() {
    if (this.prevButton) this.prevButton.addEventListener('click', this._onPrevCapture, true);
    if (this.nextButton) this.nextButton.addEventListener('click', this._onNextCapture, true);

    this.slider.addEventListener('scroll', this._onScrollCapture, { passive: true, capture: true });

    if (this._hotspots.length) {
      this._hotspots.forEach((btn) => {
        btn.addEventListener('click', this._onHotspotClickCapture, true);
        btn.addEventListener('keydown', this._onHotspotKeydownCapture, true);
      });
    }
  }

  _unbindCaptureInterceptors() {
    if (this.prevButton) this.prevButton.removeEventListener('click', this._onPrevCapture, true);
    if (this.nextButton) this.nextButton.removeEventListener('click', this._onNextCapture, true);

    if (this.slider) this.slider.removeEventListener('scroll', this._onScrollCapture, true);

    if (this._hotspots.length) {
      this._hotspots.forEach((btn) => {
        btn.removeEventListener('click', this._onHotspotClickCapture, true);
        btn.removeEventListener('keydown', this._onHotspotKeydownCapture, true);
      });
    }
  }

  _initFromMarkup() {
    this._cacheNodes();

    const currentIdx = this._hotspots.findIndex((b) => b.getAttribute('aria-current') === 'true');
    const initial = currentIdx >= 0 ? currentIdx : 0;

    this.setActiveIndex(initial, { scroll: false });
  }

  clampIndex(i) {
    const max = (this._slides?.length || 1) - 1;
    return Math.max(0, Math.min(max, i));
  }

  getNearestIndexFromCenter() {
    const slides = this._slides?.length ? this._slides : this._getSlides();
    if (!slides.length) return 0;

    const rect = this.slider.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;

    let best = 0;
    let bestDelta = Infinity;

    slides.forEach((el, idx) => {
      const r = el.getBoundingClientRect();
      const c = r.left + r.width / 2;
      const d = Math.abs(c - centerX);
      if (d < bestDelta) {
        bestDelta = d;
        best = idx;
      }
    });

    return best;
  }

  setActiveIndex(index, { scroll = true, source = 'program' } = {}) {
    this._cacheNodes();

    const i = this.clampIndex(index);
    this._activeIndex = i;

    const slides = this._slides;

    slides.forEach((el, idx) => el.classList.toggle('is-active', idx === i));

    if (this._hotspots?.length) {
      this._hotspots.forEach((btn, idx) => btn.setAttribute('aria-current', idx === i ? 'true' : 'false'));
    }

    if (scroll) {
      const token = ++this._rmNavToken;

      if (source === 'user') {
        this._rmIgnoreScrollSelectionUntil = Date.now() + 900;
      }

      const slide = slides[i];
      if (slide?.scrollIntoView) {
        slide.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      } else if (slide) {
        const left = slide.offsetLeft - (this.slider.clientWidth - slide.clientWidth) / 2;
        this.slider.scrollTo({ left, behavior: 'smooth' });
      }

      window.setTimeout(() => {
        if (token !== this._rmNavToken) return;
        this._rmIgnoreScrollSelectionUntil = 0;
      }, 950);
    }

    this.updateButtonsForSelection();
  }

  updateButtonsForSelection() {
    const max = this.clampIndex(Number.MAX_SAFE_INTEGER);
    if (this.prevButton) this.prevButton.disabled = this._activeIndex <= 0;
    if (this.nextButton) this.nextButton.disabled = this._activeIndex >= max;
  }

  _onPrevCapture(e) {
    e.preventDefault();
    e.stopImmediatePropagation();
    this.setActiveIndex(this._activeIndex - 1, { scroll: true, source: 'user' });
  }

  _onNextCapture(e) {
    e.preventDefault();
    e.stopImmediatePropagation();
    this.setActiveIndex(this._activeIndex + 1, { scroll: true, source: 'user' });
  }

  _onHotspotClickCapture(e) {
    const btn = e.currentTarget;
    const idx = this._hotspots.indexOf(btn);
    if (idx < 0) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    this.setActiveIndex(idx, { scroll: true, source: 'user' });
  }

  _onHotspotKeydownCapture(e) {
    if (e.code?.toUpperCase() !== 'ENTER' && e.code?.toUpperCase() !== 'SPACE') return;
    e.preventDefault();
    e.stopImmediatePropagation();
    this._onHotspotClickCapture(e);
  }

  _onScrollCapture() {
    window.clearTimeout(this._rmScrollT);

    this._rmScrollT = window.setTimeout(() => {
      if (Date.now() < this._rmIgnoreScrollSelectionUntil) return;

      const nearest = this.getNearestIndexFromCenter();
      if (nearest === this._activeIndex) return;

      this.setActiveIndex(nearest, { scroll: false, source: 'scroll' });
    }, 120);
  }

  update() {
    super.update?.();
    this.updateButtonsForSelection();
  }
}

customElements.define('rm-hotspots-slider', RmHotspotsSliderComponent);
